/**
 * Формы ответов админского API. Держатся вручную рядом с
 * `apps/api/src/admin/*` — общего пакета контрактов у проекта пока нет,
 * а заводить его ради одного потребителя рано.
 */

import type { GameConfigKey, GenerationStatus } from '@lust/shared'

export type Currency = 'gems' | 'gold' | 'energy'
export type ContentSource = 'seed' | 'admin'

export interface AdminAccount {
  id: string
  email: string
  role: 'admin'
  createdAt: string
}

export interface LoginResponse {
  accessToken: string
  expiresIn: number
  tokenType: 'Bearer'
  user: { id: string; email: string; role: string }
}

export interface ConfigRow {
  key: string
  value: unknown
  version: number
  source: ContentSource
  updatedBy: string | null
  updatedByEmail: string | null
  updatedAt: string
  /** Что лежит в content/*.json. null — файлы не прочитались или ключа в них нет. */
  fileValue: unknown
}

export interface ConfigsResponse {
  keys: GameConfigKey[]
  configs: ConfigRow[]
  /** Почему не удалось прочитать файлы. null — прочитались. */
  filesError: string | null
}

/**
 * Шаблон сцены в разделе Scenes. Правится из него ОДНО поле —
 * `affinityRequired`: порог симпатии, которым калибруется развилка среза.
 */
export interface SceneRow {
  id: string
  chapterId: string
  chapterTitle: string
  slot: number
  title: string
  affinityRequired: number
  clipCount: number
  source: ContentSource
  updatedBy: string | null
  updatedAt: string
}

export interface ScenesResponse {
  scenes: SceneRow[]
}

export interface ReclaimResult {
  table: string
  key: string
  status: 'reclaimed' | 'already_seed' | 'not_in_files' | 'not_in_db'
  before: unknown
  after: unknown
}

export interface PlayerRow {
  id: string
  email: string
  role: 'player' | 'admin'
  banned: boolean
  accountLevel: number
  createdAt: string
  ageConfirmedAt: string | null
  gems: number
  gold: number
  energy: number
}

export interface PlayersResponse {
  total: number
  players: PlayerRow[]
}

export interface WalletView {
  gems: number
  gold: number
  energy: number
  energyCap: number
  nextEnergyAt: string | null
}

export interface PlayerCard {
  profile: {
    id: string
    email: string
    role: 'player' | 'admin'
    banned: boolean
    locale: string
    geo: string | null
    accountLevel: number
    accountXp: number
    vipPoints: number
    monthlyCardUntil: string | null
    ageConfirmedAt: string | null
    /** Аккаунт обезличен по просьбе игрока (E11). null — обычный аккаунт. */
    anonymizedAt: string | null
    createdAt: string
  }
  wallet: WalletView | null
  progress: {
    levelId: string
    levelTitle: string
    chapterId: string
    chapterTitle: string
    ord: number
    stars: number
    clearedAt: string
  }[]
  assistants: {
    chapterId: string
    chapterTitle: string
    heroineId: string
    heroineName: string
    archetype: string
    affinity: number | null
    chosenAt: string
  }[]
  heroines: {
    heroineId: string
    name: string
    archetype: string
    copies: number
    level: number
    affinity: number
  }[]
}

/**
 * Ответ сброса игрока до новичка. Все числа приходят с сервера намеренно:
 * «сделано» без цифр непроверяемо, а часть удалённого (ролики, движения
 * по кошельку) стоила денег.
 */
export interface ResetResponse {
  userId: string
  counts: {
    levels: number
    scenes: number
    chatMessages: number
    tutorials: number
    dailyClaims: number
    heroines: number
    deck: boolean
    battles: number
    cheatAttempts: number
  }
  /** Новый баланс — только если сбрасывали кошелёк. */
  wallet: { gems: number; gold: number; energy: number } | null
  walletIdempotencyKey: string | null
  assistant: {
    heroineId: string
    clips: number
    photos: number
    skipped: { path: string; reason: string }[]
  } | null
}

export interface LedgerRow {
  id: number
  currency: Currency
  delta: number
  reason: string
  refId: string | null
  idempotencyKey: string | null
  createdAt: string
}

export interface LedgerResponse {
  rows: LedgerRow[]
  totals: { gems: number; gold: number; energy: number }
}

export interface GrantResponse {
  balances: { gems: number; gold: number; energy: number }
  idempotencyKey: string
}

export interface CheatRow {
  id: number
  createdAt: string
  userId: string
  email: string | null
  battleId: string | null
  levelId: string | null
  battleStatus: string | null
  serverResult: string | null
  serverTurns: number | null
  moveCount: number | null
  reason: string
  expectedChecksum: string | null
  gotChecksum: string
  details: string | null
}

export interface AuditRow {
  id: number
  createdAt: string
  adminId: string | null
  adminEmail: string | null
  action: string
  entity: string
  entityId: string | null
  before: unknown
  after: unknown
}

/* ─────────────────────────── раздел «Генерация» ─────────────────────────── */
// Формы ответов ниже списаны с фактического кода задач 2–7
// (`apps/api/src/admin/generation-*.ts`), а не с брифа задачи 8: контракты
// разошлись за семь задач, и брифу в этом месте нельзя верить.

export interface MuseboxStatus {
  enabled: boolean
  balance: number | null
  plan: { name: string; concurrentJobs: number | null; allowedTypes: string[] } | null
  queue: { queued: number; running: number; ready: number; failed: number }
  /** Ролики без reviewed_at по всей базе. Есть в ответе даже без ключа партнёра. */
  unreviewed: number
  warnings: string[]
}

export interface TemplateRow {
  id: string
  name: string
  description: string | null
  /** Кадр витрины партнёра — превью шаблона. null — партнёр его не прислал. */
  posterUrl: string | null
  /** Беззвучный пример ролика на CDN партнёра. null — превью нет. */
  videoUrl: string | null
  price: number | null
  tags: string[]
  duration7sEnabled: boolean
  duration10sEnabled: boolean
  /** Шаблону нужен текстовый промпт — в контенте такого поля нет, не выбирается. */
  needPrompt: boolean
  /** id клипов, которым назначен этот шаблон. Пусто — нигде не используется. */
  usedBy: string[]
}

export interface TemplatesResponse {
  templates: TemplateRow[]
  warnings: string[]
}

/**
 * Состояние ролика уровня. Шесть значений, а не пять из `GenerationStatus`
 * (`@lust/shared`) — там нет `'none'`: у клипа может не быть вовсе ни одной
 * задачи, и это законное седьмое… то есть шестое состояние дерева уровней.
 */
/**
 * ЦЕЛЬ НАСТРОЙКИ: «Все характеры» или один характер ассистентки.
 *
 * Героиней цель больше не бывает. Пока ею была студийная героиня, владелец
 * назначал шаблоны персонажу, которого ни один игрок в роли ассистентки
 * не видит, — и получал в игре вечное «она готовится…».
 *
 * Список дублирует `PRESET_TARGETS` из `@lust/shared` намеренно: админка
 * из общего пакета не собирается (у неё свой `api/types.ts` целиком —
 * это описание ЧУЖОГО ответа, а не общий код). Разойтись они могут только
 * вместе с новым характером, и тогда лишняя вкладка просто не найдёт
 * шаблонов — заметно сразу.
 */
export const PRESET_TARGETS = ['all', 'shy', 'dominant', 'playful'] as const
export type PresetTarget = (typeof PRESET_TARGETS)[number]

/** Подписи целей — те же слова, что видит игрок при создании ассистентки. */
export const PRESET_TARGET_TITLES: Record<PresetTarget, string> = {
  all: 'Все характеры',
  shy: 'Скромная',
  dominant: 'Властная',
  playful: 'Игривая',
}

/** Один ролик уровня: каким шаблоном его снимут для выбранной цели. */
export interface LevelClipNode {
  clipId: string
  ord: number
  promptPresetId: string | null
  /**
   * true — шаблон достался от цели «Все характеры», своего у характера нет.
   * Без пометки владелец решил бы, что уже настроил характер персонально,
   * когда он ещё не начинал.
   */
  inherited: boolean
  source: ContentSource
  /**
   * Ролик доснят из админки, а не описан в файле сценария.
   *
   * Не то же самое, что `source: 'admin'`: у файлового ролика владение
   * строкой переходит человеку от первой же смены шаблона, но убрать его
   * из админки всё равно нельзя — сид вернёт его на ближайшей выкатке.
   * Нижнюю границу набора панель считает по этому полю.
   */
  addedByAdmin: boolean
}

export interface LevelNode {
  /*
   * Уровень у строки есть всегда: сцен, открытых до первого боя, больше
   * нет (решение владельца 08.09.2026).
   */
  levelId: string
  levelOrd: number
  levelTitle: string
  /** null — уровень сцен не выдаёт; строка рисуется без кнопок. */
  sceneId: string | null
  sceneTitle: string | null
  /** Ролики уровня в порядке показа. */
  clips: LevelClipNode[]
}

export interface ChapterNode {
  id: string
  ord: number
  title: string
  levels: LevelNode[]
}

export interface LevelsResponse {
  chapters: ChapterNode[]
  /** Чьё это дерево: «Все характеры» или один характер. */
  target: PresetTarget
}

/** `PUT /admin/generation/scenes/:id/clips` — набор после сохранения. */
export interface SceneSetResponse {
  target: PresetTarget
  clips: LevelClipNode[]
}

export interface OrderClipResult {
  jobId: string
  status: GenerationStatus
  credits: number | null
}

/** `GET /admin/generation/heroines` — список для вкладки «Героини» и выбора на «Уровнях». */
export interface HeroineRow {
  id: string
  name: string
  archetype: string
  /** Уже подписанная ссылка (правило №5) или null — портрета нет. */
  portraitUrl: string | null
  /**
   * Префикс папки на зоне, НЕ файл (`own_…/pack/`) — сервер намеренно не
   * подписывает директорию. Чисто информационный: сам по себе НЕ признак
   * «фото есть» — колонка переживает удаление пака (`purgeHeroine` с
   * `photos: true` её не обнуляет). Признак «фото есть» — `photoUrl`.
   */
  photoPackUrl: string | null
  /**
   * Подписанная ссылка на РЕАЛЬНЫЙ первый файл пака (правило №5), или
   * null — на зоне нет ни одной картинки (или хранилище не настроено).
   * Именно это поле, а не `photoPackUrl`, решает, показывать ли красный
   * баннер «фото нет: заказать ролик нельзя» (раунд правок 1, задача 8).
   */
  photoUrl: string | null
}

export interface HeroinesResponse {
  heroines: HeroineRow[]
}

/** `DELETE /admin/heroines/:id/media` — массовое удаление роликов героини. */
export interface PurgeHeroineResult {
  ok: true
  clips: number
  photos: number
  skipped: { path: string; reason: string }[]
}
